import React, { useEffect, useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  IntlShape,
} from 'react-intl';

import { PageWrapper, SubTitle } from 'src/components/styled';
import ConfirmationModal from 'src/components/Modal/ConfirmationModal';
import SiteGroupModal from './SiteGroupModal';
import { DEFAULT_PAGINATION } from 'src/utils/constants';
import {
  exportCsvData,
  getCurrentOrder,
  getSiteGroupsColumns,
  getSiteGroupsTableActions,
  tenantModalModes,
} from './utils';
import { MODAL_STATUS } from 'src/components/Modal/constants';
import { deepEqual } from 'src/utils/comparators';
import Connector, { PropsFromRedux } from '../containers/container';
import { useCalculateTableHeight } from 'src/utils/hooks/useCalculateTableHeight';
import { ExportCsvButtonCustom } from 'src/components/CrudCommonComponents/styled';
import { prepareFiltersForQueryParamsObj } from 'src/services/utils';
import { CustomerType } from 'src/routes/Customers/modules/types';
import { TablePaginationConfig, TableProps } from 'antd';
import { FilterValue } from 'antd/es/table/interface';
import { StyledTable } from 'src/routes/SitesGroups/components/styled';
import { SiteGroupType } from 'src/services/types';
import { SorterResult } from 'antd/lib/table/interface';
import { AddOrEditGroupsProps } from 'src/redux/data/siteGroups/modules/types';

export type Props = PropsFromRedux & {
  intl: IntlShape;
  horizontalLayout: boolean;
  currentCustomer: CustomerType | null | undefined;
};

const SiteGroupsPage = ({
  siteGroupsList,
  getSiteGroupsTableData,
  getOperators,
  operatorsList,
  getCustomers,
  customersList,
  siteGroupsCount,
  deleteSiteGroup,
  createSiteGroup,
  editSiteGroup,
  excludedFromDeleteSitesGroups,
  isLoading,
  modalStatus,
  setModalStatus,
  horizontalLayout = false,
  currentCustomer = null,
  intl,
}: Props) => {
  const [isModalVisible, setisModalVisible] = useState(false);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [modalMode, setModalMode] = useState<string>('');
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState<boolean>(false);
  const [activeActionRow, setActiveActionRow] = useState<SiteGroupType | null>(
    null,
  );
  const [tableData, setTableData] = useState<SiteGroupType[]>([]);
  const [tableParams, setTableParams] = useState({
    current: DEFAULT_PAGINATION.PAGE,
    pageSize: 2 * DEFAULT_PAGINATION.LIMIT_50,
    showSizeChanger: false,
    searchKeyword: '',
    sortBy: {
      columnKey: 'groupName',
      order: 'ASC',
    },
  });
  const serverSortingColumns = [
    'groupName',
    'customerName',
    'devicesCount',
    'connectedAssigned',
    'connectedUnassigned',
    'disconnectedAssigned',
    'disconnectedUnassigned',
    'noConsent',
    'currentUtilization',
    'potentialUtilization',
    'utilizationGap',
  ];
  const columns = getSiteGroupsColumns(
    intl,
    messages,
    currentCustomer,
    serverSortingColumns,
  );

  const tableHeight = useCalculateTableHeight(currentCustomer);

  const actions = getSiteGroupsTableActions(
    intl,
    messages,
    excludedFromDeleteSitesGroups,
    setIsConfirmationModalVisible,
    setActiveActionRow,
    setisModalVisible,
    setModalMode,
  );

  const handleSearch = (searchKeyword: string) => {
    setTableParams(prevState => ({
      ...prevState,
      searchKeyword,
      current: 1,
    }));
  };

  useEffect(() => {
    getSiteGroupsTableData({
      page: tableParams.current - 1,
      limit: tableParams.pageSize,
      search: tableParams.searchKeyword,
      sortBy: tableParams.sortBy,
      cache: currentCustomer?.id ? 'true' : 'false',
      customerID: currentCustomer?.id,
    });
  }, [tableParams, getCustomers, getSiteGroupsTableData, currentCustomer?.id]);

  useEffect(() => {
    if (!isLoading) {
      setTableData(
        siteGroupsList.map(siteGroup => ({
          key: siteGroup.id,
          ...siteGroup,
        })),
      );
    }
  }, [siteGroupsList, isLoading]);

  const handleTableChange: TableProps<SiteGroupType>['onChange'] = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter,
    _extra,
  ) => {
    let sortBy = {
      columnKey: 'groupName',
      order: 'ASC',
    };
    if (
      serverSortingColumns.includes(
        (sorter as SorterResult<SiteGroupType>).columnKey as string,
      )
    ) {
      sortBy = {
        columnKey: ((sorter as SorterResult<SiteGroupType>).columnKey ??
          '') as string,
        order: getCurrentOrder(sorter),
      };
    }
    setTableParams(prevState => ({
      ...prevState,
      current: pagination?.current ?? prevState.current,
      sortBy,
    }));
  };

  useEffect(() => {
    if (modalStatus !== MODAL_STATUS.SUCCESS) {
      return;
    }
    getSiteGroupsTableData({
      page: tableParams.current - 1,
      limit: tableParams.pageSize,
      search: tableParams.searchKeyword,
      sortBy: tableParams.sortBy,
      cache: currentCustomer?.id ? 'true' : 'false',
      customerID: currentCustomer?.id,
    });
  }, [currentCustomer?.id, modalStatus, getSiteGroupsTableData, tableParams]);

  return (
    <PageWrapper>
      <SubTitle>
        <FormattedMessage
          {...messages.siteGroupsCounter}
          values={{ siteGroupsCount: siteGroupsCount }}
        />
      </SubTitle>
      <StyledTable
        sticky={true}
        scroll={{ y: tableHeight }}
        horizontalLayout={horizontalLayout}
        withCsvExport={true}
        customExportButton={
          <ExportCsvButtonCustom
            isLoading={isCsvLoading}
            disabled={isCsvLoading}
            onClick={() =>
              exportCsvData(
                {
                  ...tableParams,
                  filters: prepareFiltersForQueryParamsObj({
                    customerId: currentCustomer ? currentCustomer?.id : null,
                  }),
                },
                setIsCsvLoading,
              )
            }
          >
            {intl.formatMessage(messages.exportCsvButtonLabel)}
          </ExportCsvButtonCustom>
        }
        loading={isLoading}
        data={tableData}
        pagination={
          siteGroupsCount > tableParams?.pageSize
            ? { ...tableParams, total: siteGroupsCount }
            : false
        }
        onChange={handleTableChange}
        columns={columns.filter(elem => !!elem)}
        actions={actions}
        withSearch
        searchFunction={handleSearch}
        searchPlaceholder={intl.formatMessage(messages.searchPlaceholder)}
        withAddButton
        addButtonText={intl.formatMessage(messages.addButtonText)}
        addButtonOnClick={() => {
          setActiveActionRow(null);
          setisModalVisible(true);
          setModalMode(tenantModalModes.ADD);
        }}
      />
      <ConfirmationModal
        isModalVisible={isConfirmationModalVisible}
        setIsModalVisible={(value: boolean) =>
          setIsConfirmationModalVisible(value)
        }
        onOk={() => {
          deleteSiteGroup({
            groupId: (activeActionRow as SiteGroupType).id,
            page: tableParams.current - 1,
            limit: tableParams.pageSize,
            customerID: currentCustomer?.id,
            searchKeyword: tableParams.searchKeyword,
          });
        }}
        message={undefined}
        title={undefined}
        onCancel={undefined}
      />
      {isModalVisible && (
        <SiteGroupModal
          currentCustomer={currentCustomer}
          operatorsList={operatorsList}
          customersList={customersList}
          getOperators={getOperators}
          getCustomers={getCustomers}
          isModalVisible={isModalVisible}
          setIsModalVisible={(value: boolean) => {
            setisModalVisible(value);
          }}
          onOk={(tenantData: AddOrEditGroupsProps) => {
            if (modalMode === tenantModalModes.ADD) {
              createSiteGroup(tenantData);
              return;
            }
            editSiteGroup({
              id: (activeActionRow as SiteGroupType).id,
              data: tenantData,
            });
          }}
          mode={modalMode}
          modalStatus={modalStatus}
          setModalStatus={setModalStatus}
          tenantListRow={activeActionRow}
        />
      )}
    </PageWrapper>
  );
};

const messages = defineMessages({
  siteGroupsCounter: {
    id: 'components.SiteGroup.siteGroupsCounter',
    defaultMessage:
      '{siteGroupsCount } {siteGroupsCount, select, 1 {Sites Group} other {Sites Groups}}',
  },
  tenants: {
    id: 'components.SiteGroup.tenants',
    defaultMessage: 'Sites',
  },
  searchPlaceholder: {
    id: 'components.SiteGroup.searchPlaceholder',
    defaultMessage: 'Search by name',
  },
  loggedInToTenantAlert: {
    id: 'components.SiteGroup.loggedInToTenantAlert',
    defaultMessage:
      'You are trying to delete the site you are logged in to. This operation is not allowed.',
  },
  name: {
    id: 'components.SiteGroup.name',
    defaultMessage: 'Group Site Name',
  },
  enableCPX: {
    id: 'components.SiteGroup.enableCPX',
    defaultMessage: 'CPX Enabled',
  },
  address: {
    id: 'components.SiteGroup.address',
    defaultMessage: 'Address',
  },
  delete: {
    id: 'components.SiteGroup.delete',
    defaultMessage: 'Delete',
  },
  edit: {
    id: 'components.SiteGroup.edit',
    defaultMessage: 'Edit',
  },
  contactName: {
    id: 'components.SiteGroup.contactName',
    defaultMessage: 'Contact name',
  },
  contactEmail: {
    id: 'components.SiteGroup.contactEmail',
    defaultMessage: 'Contact Email',
  },
  contactPhone: {
    id: 'components.SiteGroup.contactPhone',
    defaultMessage: 'Contact phone',
  },
  addButtonText: {
    id: 'components.SiteGroup.addButtonText',
    defaultMessage: '+ Add Group',
  },
  customerLabel: {
    id: 'components.SiteGroup.customerLabel',
    defaultMessage: 'Customer',
  },
  tenantTypeLabel: {
    id: 'components.SiteGroup.tenantTypeLabel',
    defaultMessage: 'Type',
  },
  devicesLabel: {
    id: 'components.SiteGroup.devicesLabel',
    defaultMessage: 'Total Devices',
  },
  connectedAndAssignedLabel: {
    id: 'components.SiteGroup.connectedAndAssignedLabel',
    defaultMessage: 'Connected & Assigned',
  },
  connectedAndUnassignedLabel: {
    id: 'components.SiteGroup.connectedAndUnassignedLabel',
    defaultMessage: 'Connected & Unassigned',
  },
  disconnectedAndAssignedLabel: {
    id: 'components.SiteGroup.disconnectedAndAssignedLabel',
    defaultMessage: 'Disconnected & Assigned',
  },
  disconnectedAndUnassignedLabel: {
    id: 'components.SiteGroup.disconnectedAndUnassignedLabel',
    defaultMessage: 'Disconnected & Unassigned',
  },
  noConsentLabel: {
    id: 'components.SiteGroup.noConsentLabel',
    defaultMessage: 'No Consent',
  },
  monitoringLabel: {
    id: 'components.SiteGroup.monitoringLabel',
    defaultMessage: 'Monitoring',
  },
  currentUtilization: {
    id: 'components.SiteGroup.currentUtilization',
    defaultMessage: 'Current Utilization',
  },
  potentialUtilization: {
    id: 'components.SiteGroup.potentialUtilization',
    defaultMessage: 'Potential Utilization',
  },
  gapUtilization: {
    id: 'components.SiteGroup.gapUtilization',
    defaultMessage: 'Utilization Gap',
  },
  exportCsvButtonLabel: {
    id: 'Tenants.Component.exportCsvButtonLabel',
    defaultMessage: 'Export CSV',
  },
});
export default Connector(
  injectIntl(
    React.memo(SiteGroupsPage, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
